import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Subscription, skip } from 'rxjs';
import { ScribeService } from '../../services/stt-chunked/scribe.service';
import { SessionInactivityService } from '../../services/session-inactivity/session-inactivity.service';
import { CVANotificationService } from '../../services/cva-notification/cva-notification.service';
import { NotificationClassEnum } from '../../models/notification-class.enum';
import { SttUIStatus } from '../../models/stt-chunked.model';
import { MESSAGES } from '../../util/constants';


@Component({
  selector: 'cva-speech-to-text-chunked',
  standalone: false,
  templateUrl: './speech-to-text-chunked.component.html',
  styleUrl: './speech-to-text-chunked.component.scss',
})

export class SpeechToTextChunkedComponent implements OnInit, OnChanges, OnDestroy {
  public recording: boolean = false;
  public loading: boolean = false;
  // Purely a UI cue — never concatenated into transcription/dictation text.
  public dictationStatus: SttUIStatus = '';
  public hours: number = 0;
  public minutes: number = 0;
  public seconds: number = 0;
  public interval: number = 0;
  public currentDate = new Date();
  @Input() recordedTime: string = '00:00:00';

  @Input() startRecordingLabel = 'Start Recording';
  @Input() stopRecordingLabel = 'Stop Recording';
  @Input() dateFormat = 'EEE d MMM y';
  @Input() recordingTimeLabel = 'Recording Time';
  @Input() showDate = true;
  @Input() isDectation = false;
  @Output() sendRecordedText: EventEmitter<string> = new EventEmitter();
  @Output() startAndStop: EventEmitter<string> = new EventEmitter();
  @Output() recognizing: EventEmitter<string> = new EventEmitter();
  // 'listening' while capturing, 'processing' while the final chunk is
  // still uploading after Stop, '' when there's nothing to show. Parent
  // owns display/wording since it owns the transcript block.
  @Output() speechStatus: EventEmitter<SttUIStatus> = new EventEmitter();
  @Input() showLabel = false;
  // optional per-instance chunk window; unset → environment values
  @Input() minRecordingSeconds?: number;
  @Input() maxRecordingSeconds?: number;

  private subs: Subscription[] = [];

  // ScribeService is a root singleton shared by EVERY instance of this component.
  // Only the instance that called start() may react to transcript/stop/error/pending
  // events — otherwise text lands in all instances, a stopRec from the parent kills
  // someone else's recording, and destroying one instance discards another's session.
  // Set when start() is requested; released when the status returns to '' (finished,
  // errored or discarded).
  private ownsSession = false;
  private destroyed = false;

  // Wall-clock timer state. A setInterval tick counter under-counts in throttled
  // background tabs, so elapsed time is derived from Date.now() instead.
  private timerStartMs = 0;
  private timerBaseSeconds = 0;

  private setStatus(status: SttUIStatus): void {
    this.dictationStatus = status;
    if (status === '') this.ownsSession = false; // session fully over — free the singleton for other instances
    this.speechStatus.emit(status);
  }


  constructor(
    private scribeService: ScribeService,
    private sessionInactivityService: SessionInactivityService,
    private cvaNotificationService: CVANotificationService,
  ) { }

  ngOnInit(): void {
    this.subs.push(
      this.listenTranscript(),
      this.listenErrors(),
      this.listenToStop(),
      this.listenWarnings(),
      this.listenPending(),
      this.listenDeviceDisconnected(),
      this.listenSilentChunk()
    );
  }

  // Clears "Finishing up" the moment the last in-flight chunk actually
  // completes — not just when stop() was called. If there was nothing
  // left to flush, pending$ is already 0 and status clears immediately.
  listenPending() {
    return this.scribeService.pending$.subscribe({
      next: (count: number) => {
        if (this.ownsSession && this.dictationStatus === 'processing' && count === 0) {
          this.setStatus('');
        }
      },
      error: (e) => console.error(e),
    })
  }

  listenTranscript() {
    // transcript$ is a BehaviorSubject on a root-singleton service — it
    // replays whatever it last held from a PREVIOUS session the instant
    // we subscribe (before this component even calls start()). skip(1)
    // discards that replay; only real new emissions get through.
    return this.scribeService.transcript$.pipe(skip(1)).subscribe({
      next: (text: string) => {
        // Not our session (another instance is dictating) → ignore.
        if (!this.ownsSession) return;
        if (text) {
          // POC only has final text per chunk, no live partial. Consumer's
          // recognizingText()/recordedText() pair expect: interim event
          // appends a new line (hasNewLineAppended false -> true), final
          // event replaces that line and resets the flag for next segment.
          // Emit `recognizing` with the SAME text right before
          // `sendRecordedText` — first call creates the line, second
          // replaces it (no-op visually) and resets the flag, keeping
          // multi-chunk line breaks correct without touching the consumer.
          // this.recognizing.emit(text);
          this.sendRecordedText.emit(text);
        }
      },
      error: (e) => console.error(e),
    })
  }

  listenErrors() {
    return this.scribeService.error$.subscribe({
      next: (message: string) => {
        if (!this.ownsSession) return; // someone else's start() failed
        console.error('[STT] error from ScribeService:', message);
        this.loading = false;
        this.recording = false;
        this.setStatus('');
        this.stopTimer();
        this.sessionInactivityService.setSpeechInProgress(false);
        this.cvaNotificationService.showNotification(
          message,
          NotificationClassEnum.ERROR
        );
      },
    })
  }

  // Non-fatal — dropped chunk, recording session keeps going. Uses INFO
  // class since a dedicated WARNING class wasn't confirmed to exist on
  // NotificationClassEnum — swap if one is available.
  listenWarnings() {
    return this.scribeService.warning$.subscribe({
      next: (message: string) => {
        if (!this.ownsSession) return;
        this.cvaNotificationService.showNotification(
          message,
          NotificationClassEnum.ERROR
        );
      },
    })
  }

  listenToStop() {
    return this.scribeService.stopRec.subscribe({
      next: () => {
        // stopRec is a broadcast. Act only if THIS instance is actively recording:
        //  - another instance's recording must not be discarded by us, and
        //  - a stop request arriving while we're merely 'processing' (user already
        //    pressed Stop) must NOT discard() — that clears the session id and throws
        //    away the final transcript that is still uploading.
        if (!this.ownsSession || !this.recording) {
          console.debug('[STT] stopRec ignored (not the recording instance)');
          return;
        }
        this.stopRecording(false, true);
      },
      error: (e) => console.error(e),
    })
  }

  // Mic unplugged / audio engine lost. RecordingService already flushed + tore down;
  // without this the UI kept showing "recording", the timer kept running and the
  // session-inactivity guard stayed suppressed until the user clicked Stop.
  listenDeviceDisconnected() {
    return this.scribeService.deviceDisconnected$.subscribe({
      next: () => {
        if (!this.ownsSession || !this.recording) return;
        console.warn('[STT] input device disconnected — resetting UI');
        // Normal stop path: resets flags/timer, emits 'stopped', and shows
        // 'processing' if the final flushed chunk is still uploading.
        this.stopRecording();
        this.cvaNotificationService.showNotification(
          'Microphone disconnected. Recording has been stopped.',
          NotificationClassEnum.ERROR
        );
      },
      error: (e) => console.error(e),
    })
  }

  listenSilentChunk() {
    return this.scribeService.silentChunk$.subscribe({
      next: ({ limitReached }) => {
        if (!this.ownsSession || !this.recording) return;
        if (limitReached) {
          console.warn('[STT] auto-stop: consecutive chunks without speech');
          this.stopRecording(); // normal stop: resets timer/flags, emits 'stopped'
          this.cvaNotificationService.showNotification(MESSAGES.AUDIO_NO_SPEECH_AUTO_STOPPED, NotificationClassEnum.ERROR);
        } else {
          this.cvaNotificationService.showNotification(MESSAGES.AUDIO_NO_SPEECH_WARNING, NotificationClassEnum.WARNING);
        }
      },
      error: (e) => console.error(e),
    })
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['recordedTime']) {
      const { currentValue, previousValue } = changes['recordedTime'];
      if (previousValue !== currentValue) {
        this.setRecordedTime(currentValue)
      }
    }
  }

  startRecording() {
    this.loading = true;
    // Claim the shared service BEFORE awaiting so error$/transcript$ events that
    // fire during start() are attributed to this instance.
    this.ownsSession = true;
    this.scribeService.start({
      minRecordingSeconds: this.minRecordingSeconds,
      maxRecordingSeconds: this.maxRecordingSeconds,
    }).then((started) => {
      // start() resolves (not rejects) on mic denial — it routes the error to
      // error$ and returns false. It also returns false when the call was ignored
      // (already recording / another start in progress) and nothing else would
      // clear our spinner, so reset here too.
      if (!started) {
        console.warn('[STT] start() did not begin a recording — resetting button state');
        this.loading = false;
        this.ownsSession = false;
        return;
      }
      // Component was destroyed while the mic permission prompt was open → the
      // session now belongs to nobody. Release the mic instead of orphaning it.
      if (this.destroyed) {
        console.warn('[STT] component destroyed during start() — discarding orphaned session');
        this.scribeService.discard();
        return;
      }
      this.calculateRecordingTime();
      this.recording = true;
      this.setStatus('listening');
      this.sessionInactivityService.setSpeechInProgress(true);
      this.loading = false;
      this.startAndStop.next('started');
      console.log("Start Recording Clicked");
    }).catch((e) => {
      console.error('[STT] startRecording failed', e);
      this.loading = false;
      this.ownsSession = false;
      // showNotification expects a string — e may be an Error object.
      this.cvaNotificationService.showNotification(
        e instanceof Error ? e.message : String(e),
        NotificationClassEnum.ERROR
      );
      // (previously rethrown here → surfaced as an unhandled promise rejection)
    });
  }


  stopRecording(isPaused = false, isDiscard = false) {
    this.loading = true;

    if (isDiscard) {
      this.scribeService.discard()
    } else if (isPaused) {
      this.scribeService.pause();
    } else {
      this.scribeService.stop();
    }
    this.stopTimer();
    this.recording = false;
    this.sessionInactivityService.setSpeechInProgress(false);
    this.loading = false;
    this.startAndStop.next('stopped');
    // stop() synchronously flushes any buffered audio into the pipeline,
    // so pending$ already reflects whether there's a final chunk in
    // flight by the time we check it here.
    // After a DISCARD the session id is cleared, so nothing in flight will ever
    // be shown — never put the button into a 'processing' state for it.
    const processing = !isDiscard && this.scribeService.pending$.value > 0;
    this.setStatus(processing ? 'processing' : '');
    console.log(`Stop Recording Clicked (discard=${isDiscard}, processing=${processing})`);
  }

  calculateRecordingTime() {
    // Clear any previous interval first so a double call can't leak a second,
    // orphaned timer that keeps ticking forever.
    this.stopTimer();
    this.timerBaseSeconds = this.hours * 3600 + this.minutes * 60 + this.seconds;
    this.timerStartMs = Date.now();
    this.interval = window.setInterval(() => {
      this.updateTime();
    }, 1000);
  }

  updateTime() {
    // Derive from the wall clock — counting ticks drifts when the tab is throttled.
    const total = this.timerBaseSeconds + Math.floor((Date.now() - this.timerStartMs) / 1000);
    this.hours = Math.floor(total / 3600);
    this.minutes = Math.floor((total % 3600) / 60);
    this.seconds = total % 60;

    this.recordedTime = this.formatTime();
  }

  formatTime(): string {
    const pad = (num: number) => num.toString().padStart(2, '0');
    return `${pad(this.hours)}:${pad(this.minutes)}:${pad(this.seconds)}`;
  }

  getRecordedTime(): string {
    return this.recordedTime;
  }

  setRecordedTime(time = "00:00:00") {
    const timeSplit = time.split(":");
    const [hours = '0', min = '0', sec = '0'] = timeSplit;

    this.hours = Number(hours) || 0;
    this.minutes = Number(min) || 0;
    this.seconds = Number(sec) || 0;

    // If the parent re-bases the time while we're running, re-anchor the wall clock
    // so the next tick continues from the new value.
    if (this.interval) {
      this.timerBaseSeconds = this.hours * 3600 + this.minutes * 60 + this.seconds;
      this.timerStartMs = Date.now();
    }

    this.recordedTime = this.formatTime();
  }

  stopTimer() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = 0;
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    // ScribeService/RecordingService are root singletons — they keep
    // running after this component is destroyed unless explicitly stopped.
    // Prevents orphaned mic stream / AudioContext if user navigates away
    // mid-recording.
    // Only touch the service if THIS instance owns the session (another instance
    // may be the one recording). Leaving while 'processing' also discards on purpose:
    // the parent/transcript target is going away, so the late text has nowhere to land.
    if (this.ownsSession) {
      if (this.scribeService.state$.value !== 'idle') {
        console.log('[STT] component destroyed — discarding active session');
        this.scribeService.discard();
      }
      // Previously never reset → session-inactivity timeout stayed suppressed forever.
      this.sessionInactivityService.setSpeechInProgress(false);
      this.setStatus('');
    }
    this.stopTimer();
    this.subs.forEach(s => s.unsubscribe());
  }
}
